import { io } from 'socket.io-client'
import { refreshAccessToken } from '../utils/api'

class SocketService {
  constructor() {
    this.socket = null
    this.isConnected = false
    this.listeners = new Map()
  }

  connect() {
    // Already connected/connecting for this login: reuse it (token refreshes must not drop the socket)
    if (this.socket) return this.socket

    const serverUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'
    
    this.socket = io(serverUrl, {
      // Callback form: evaluated on every (re)connect so a refreshed token is always used
      auth: (cb) => cb({
        token: localStorage.getItem('agent_token'),
        type: 'agent'
      }),
      transports: ['websocket', 'polling'],
      timeout: 10000,
    })

    this.socket.on('connect', () => {
      console.log('🔌 Connected to chat server')
      this.isConnected = true

      // Agent registration is now automatic via socket authentication
      console.log('✅ Agent will be auto-registered via socket auth')

      // Log socket ID for debugging
      console.log('🆔 Socket ID:', this.socket.id)
      this.emit('socket_connected', { socketId: this.socket.id })
    })

    this.socket.on('disconnect', (reason) => {
      console.log('📡 Disconnected from chat server:', reason)
      this.isConnected = false
    })

    this.socket.on('connect_error', async (error) => {
      console.error('❌ Socket connection error:', error)
      this.isConnected = false

      // Access token expired while offline/asleep: get a new one and retry
      if (/expired|invalid/i.test(error?.message || '') && this.socket) {
        try {
          await refreshAccessToken()
          this.socket?.connect()
        } catch (refreshError) {
          console.error('❌ Could not refresh token for socket:', refreshError)
        }
      }
    })

    this.socket.on('error', (error) => {
      console.error('❌ Socket error:', error)
    })

    // Set up event forwarding
    this.setupEventForwarding()

    return this.socket
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
      this.isConnected = false
    }
  }

  setupEventForwarding() {
    const events = [
      'message_received',
      'message_sent_confirmation',
      'typing_start',
      'typing_stop',
      'session_updated',
      'user_online',
      'user_offline',
      'new_chat_request',
      'session_assigned',
      'session_transferred',
      'agent_login_success',
      'session_joined',
      'chat_taken',
      'agent_joined',
      'chat_closed',
      'session_timeout',
      'error',
      'connect_error',
      'ai_session_started',
      'ai_activity',
      'transfer_completed',
      'mode_switched'
    ]

    events.forEach(event => {
      this.socket.on(event, (data) => {
        console.log(`📥 Agent received event: ${event}`, data)
        this.emit(event, data)
      })
    })
  }

  // Event emitter methods
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event).add(callback)
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return
    // No callback = remove every listener for the event
    if (callback) {
      this.listeners.get(event).delete(callback)
    } else {
      this.listeners.delete(event)
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error(`Error in ${event} listener:`, error)
        }
      })
    }
  }

  // Chat methods
  joinSession(sessionId) {
    if (this.socket && this.isConnected) {
      console.log(`🔗 Agent joining session: ${sessionId}`)
      this.socket.emit('agent_join_session', { sessionId })
    }
  }

  takeChat(sessionId) {
    if (this.socket && this.isConnected) {
      console.log(`✋ Agent taking chat: ${sessionId}`)
      this.socket.emit('agent_take_chat', { sessionId })
    }
  }

  leaveSession(sessionId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('agent_leave_session', { sessionId })
    }
  }

  sendMessage(message) {
    if (this.socket && this.isConnected) {
      this.socket.emit('agent_send_message', message)
    }
  }

  startTyping(sessionId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('agent_typing_start', { sessionId })
    }
  }

  stopTyping(sessionId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('agent_typing_stop', { sessionId })
    }
  }

  updateStatus(status) {
    if (this.socket && this.isConnected) {
      this.socket.emit('agent_status_update', { status })
    }
  }

  transferSession(sessionId, targetAgentId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('transfer_session', { sessionId, targetAgentId })
    }
  }

  closeSession(sessionId, reason = 'completed') {
    if (this.socket && this.isConnected) {
      this.socket.emit('close_session', { sessionId, reason })
    }
  }

  // Close chat (for compatibility with user close_chat event)
  closeChat(sessionId, reason = 'agent_ended') {
    if (this.socket && this.isConnected) {
      console.log(`🔒 Agent closing chat: ${sessionId}`)
      this.socket.emit('close_chat', { sessionId, reason })
    }
  }
}

export const socketService = new SocketService()
